/**
 * The browser's ProjectStore, kept on the server (/me/projects) with IndexedDB as a cache.
 *
 * O5, owner decision D3 (2026-09-30). A project used to live only in the IndexedDB of the browser
 * it was built in. Saddle's ads send people to build from Instagram's in-app browser or their
 * phone's, and an app built there could not be edited, undone or republished from a laptop — and
 * clearing site data lost its source for good. Now the server holds every project, and this
 * browser keeps a copy so that reading one is cheap and works for a moment without a connection.
 *
 * The rules, each for a reason:
 *
 * - READS go through: each asks the server whether this browser's copy is still current
 *   (`?known=<version>`, answered without the body when it is), so a change made on the laptop is
 *   what the phone shows next. Without a connection, the copy here answers.
 * - WRITES go to the server and name the version they were made from — the version of the copy
 *   here, which is the one the person was looking at. A copy another browser has since replaced is
 *   refused there (409), the latest version is loaded here, and the person is told to make their
 *   change again. Nothing is overwritten and nothing is merged behind their back.
 * - A chat message is the one exception: appending it to the latest version loses nothing of
 *   anybody's, so a refused one is re-applied there instead of bothering the person.
 * - WITHOUT A CONNECTION a write is REFUSED with a sentence that says so. Keeping it here to send
 *   later would be a change that exists in one browser only — the exact problem this replaces —
 *   and one that silently loses to the next edit made anywhere else.
 * - Projects built before this, which exist ONLY here, are uploaded on sign-in under their own id
 *   (so doing it twice, or in two tabs, cannot make a second copy), with their chat. One that
 *   cannot be uploaded yet stays listed and is tried again next time; nothing already built is lost.
 *
 * Writes to one project from this tab are made one at a time, each from the version the one
 * before it produced, so a chat message and an edit saved together do not refuse each other.
 */
import { nanoid } from 'nanoid/non-secure';
import type { Project } from '@/storage/ProjectRepository';
import type { ChatMessage } from '@/llm/types';
import type { ChatStore, NewProject, ProjectStore, ProjectUpdate, Stores } from '@/storage/projectStorePort';
import { duplicateDefinition } from '@/storage/duplicateDefinition';
import { ApiError } from '../api/client';
import type { ProjectsApi, ServerProject, ServerProjectSummary } from '../api/projects';
import type { AppDefinition } from '@/types/app';
import { read, write } from './indexedDb';

export const PROJECT_CHANGED_ELSEWHERE =
  'This app was changed in another tab or browser, so nothing was overwritten. The latest version is loaded now — make your change again.';
/**
 * The refusal after a save of THIS browser's timed out: it may well have landed, and the change
 * now refused was made from the version before it. Nobody else is involved, so the sentence
 * that blames another tab or browser would be false.
 */
export const LAST_SAVE_MAY_HAVE_LANDED =
  'Your last save may have gone through after all, so nothing was overwritten. What was saved is loaded now — check it, and make this change again.';
/** The refusal when the reload after a 409 finds nothing: the app is gone, not newer. */
export const PROJECT_DELETED_ELSEWHERE =
  'This app was deleted in another tab or browser, so this change was not saved.';
export const NOT_SAVED_OFFLINE = "You're offline, so this change wasn't saved. Reconnect and try again.";
export const NOT_SAVED_TIMEOUT =
  'Saving took too long, so this change may not have been saved. Reload the page to see what was.';
export const SIGN_IN_TO_SAVE = 'Sign in to save your app.';
export const OTHER_ACCOUNT = 'You are signed in as a different account now. Reload the page.';
export const PROJECT_NOT_FOUND = 'That app was not found.';

/** A write the server refused because the copy it was made from has been replaced. */
export class ProjectChangedElsewhere extends Error {
  constructor(message: string = PROJECT_CHANGED_ELSEWHERE) {
    super(message);
    this.name = 'ProjectChangedElsewhere';
  }
}

/** A write refused because the project is no longer on the server at all. */
export class ProjectDeletedElsewhere extends Error {
  constructor() {
    super(PROJECT_DELETED_ELSEWHERE);
    this.name = 'ProjectDeletedElsewhere';
  }
}

/** A write that did not reach the server, and was not kept anywhere else either. */
export class ProjectNotSaved extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ProjectNotSaved';
  }
}

/** A row of the IndexedDB `projects` store, as this cache writes it. */
interface CachedRow extends Project {
  /**
   * The server's version of this copy. ABSENT on a project that exists only in this browser —
   * built before projects moved to the server — which is what marks it for upload.
   */
  server_version?: number;
  /** The chat, kept with the project as the server keeps it. */
  messages?: ChatMessage[];
}

/** A chat message as the IndexedDB-only store kept them, in their own object store. */
interface LegacyMessageRow extends ChatMessage {
  project_id: string;
  seq: number;
}

export interface ServerStoreDeps {
  api: ProjectsApi;
  /** The signed-in account's id (the token's `sub`), or null when it cannot be read. */
  identity: () => string | null;
  /** False when the browser knows it has no connection. Defaults to `navigator.onLine`. */
  online?: () => boolean;
  newId?: () => string;
}

/** The `sub` of a Cognito access token, read without verifying it — the server verifies. */
export function subOfToken(token: string | null): string | null {
  if (!token) return null;
  const part = token.split('.')[1];
  if (!part) return null;
  try {
    const padded = part.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(part.length / 4) * 4, '=');
    const payload = JSON.parse(atob(padded)) as { sub?: unknown };
    return typeof payload.sub === 'string' && payload.sub ? payload.sub : null;
  } catch {
    return null;
  }
}

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

const isConflict = (e: unknown): boolean => e instanceof ApiError && e.kind === 'conflict';
const isUnreachable = (e: unknown): boolean => e instanceof ApiError && (e.kind === 'offline' || e.kind === 'timeout');

function toProject(row: CachedRow): Project {
  const { server_version, messages: _m, ...project } = row;
  return { ...project, version: server_version ?? project.version };
}

/**
 * A list row drawn from the server's summary, for a project this browser holds no current copy
 * of. Its definition carries ONLY what a row shows (runtime, catalogue id, address, unfinished):
 * it is never written to the cache, so opening the project still loads the whole of it — cached,
 * it would answer `?known=` as current and the editor would open an app with no source.
 */
function listRow(summary: ServerProjectSummary & { listing: NonNullable<ServerProjectSummary['listing']> }, ownerId: string): CachedRow {
  const { runtime, hostedAppId, webUrl, unfinished } = summary.listing;
  const definition = {
    name: summary.name,
    ...(runtime ? { runtime } : {}),
    ...(hostedAppId ? { hostedAppId } : {}),
    ...(webUrl ? { webUrl } : {}),
    ...(unfinished ? { unfinished: { at: summary.updatedAt, reason: '' } } : {}),
  } as unknown as AppDefinition;
  return {
    id: summary.id,
    name: summary.name,
    description: '',
    definition,
    thumbnail: null,
    created_at: summary.updatedAt,
    updated_at: summary.updatedAt,
    is_published: false,
    version: summary.version,
    owner_id: ownerId,
    server_version: summary.version,
  };
}

const byNewest = (a: CachedRow, b: CachedRow) => (a.updated_at < b.updated_at ? 1 : a.updated_at > b.updated_at ? -1 : 0);

// --- the cache ---------------------------------------------------------------------------

const cache = {
  get: (id: string) => read<CachedRow | undefined>('projects', (s) => s.get(id) as IDBRequest<CachedRow | undefined>),
  all: () => read<CachedRow[]>('projects', (s) => s.getAll() as IDBRequest<CachedRow[]>),
  put: (row: CachedRow) => write('projects', (s) => s.put(row)),
  remove: (id: string) => write('projects', (s) => s.delete(id)),

  /** The chat of a project from before, which lived in its own object store. */
  async legacyMessages(projectId: string): Promise<ChatMessage[]> {
    const rows = await read<LegacyMessageRow[]>('messages', (s) => s.getAll() as IDBRequest<LegacyMessageRow[]>);
    return rows
      .filter((r) => r.project_id === projectId)
      .sort((a, b) => a.seq - b.seq)
      .map(({ project_id: _p, seq: _s, ...message }) => message);
  },
  async dropLegacyMessages(projectId: string): Promise<void> {
    const rows = await read<LegacyMessageRow[]>('messages', (s) => s.getAll() as IDBRequest<LegacyMessageRow[]>);
    const mine = rows.filter((r) => r.project_id === projectId);
    if (mine.length === 0) return;
    await write('messages', (s) => {
      for (const row of mine) s.delete(row.id);
    });
  },
};

/** Run `fn` on each item, at most `limit` at once, keeping the order. */
async function mapLimit<T, R>(items: T[], limit: number, fn: (item: T) => Promise<R>): Promise<R[]> {
  const out = new Array<R>(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const i = next++;
      out[i] = await fn(items[i]);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return out;
}

export function createServerStores(deps: ServerStoreDeps): Stores {
  const { api } = deps;
  const online = deps.online ?? (() => typeof navigator === 'undefined' || navigator.onLine !== false);
  const newId = deps.newId ?? (() => nanoid());

  /**
   * Projects whose last save from this tab timed out, so nobody knows whether it landed. The
   * next write's 409 is then most likely that save, not another browser — see
   * LAST_SAVE_MAY_HAVE_LANDED. Cleared by any answer that settles it.
   */
  const unsure = new Set<string>();

  /** Writes to one project, one at a time. */
  const chains = new Map<string, Promise<unknown>>();
  function serially<T>(id: string, fn: () => Promise<T>): Promise<T> {
    const run = (chains.get(id) ?? Promise.resolve()).catch(() => undefined).then(fn);
    const tail = run.catch(() => undefined);
    chains.set(id, tail);
    void tail.then(() => {
      if (chains.get(id) === tail) chains.delete(id);
    });
    return run;
  }

  /** Whether `ownerId` may be answered for: signed in, and the account actually signed in. */
  function readable(ownerId: string): boolean {
    if (!ownerId) return false;
    const me = deps.identity();
    // Unknown (an expired token, say) is left to the server, which answers 401 or refreshes.
    return !me || me === ownerId;
  }

  function writable(ownerId: string): void {
    if (!ownerId) throw new ProjectNotSaved(SIGN_IN_TO_SAVE);
    const me = deps.identity();
    if (me && me !== ownerId) throw new ProjectNotSaved(OTHER_ACCOUNT);
  }

  async function owned(id: string, ownerId: string): Promise<CachedRow | undefined> {
    const row = await cache.get(id);
    return row && row.owner_id === ownerId ? row : undefined;
  }

  /** The server's copy, written here. A local-only copy it replaces takes its chat with it. */
  async function adopt(answer: ServerProject, ownerId: string, prev?: CachedRow): Promise<CachedRow> {
    const row: CachedRow = {
      id: answer.id,
      name: answer.name,
      description: answer.description ?? '',
      definition: answer.definition,
      thumbnail: prev?.thumbnail ?? null,
      created_at: answer.createdAt ?? prev?.created_at ?? answer.updatedAt,
      updated_at: answer.updatedAt,
      is_published: prev?.is_published ?? false,
      version: answer.version,
      owner_id: ownerId,
      server_version: answer.version,
      messages: answer.messages ?? [],
    };
    await cache.put(row);
    if (prev && prev.server_version === undefined) await cache.dropLegacyMessages(answer.id);
    return row;
  }

  async function messagesOf(row: CachedRow): Promise<ChatMessage[]> {
    if (row.server_version !== undefined) return row.messages ?? [];
    return [...(await cache.legacyMessages(row.id)), ...(row.messages ?? [])];
  }

  /**
   * This project as the server has it now, by way of the copy here: asked with the version held,
   * so an unchanged one costs a header's worth. Null when the server has no such project.
   */
  async function fresh(id: string, ownerId: string): Promise<CachedRow | null> {
    const cached = await owned(id, ownerId);
    // Not on the server yet: the copy here is the only one, and sign-in uploads it.
    if (cached && cached.server_version === undefined) return cached;
    if (!online() && cached) return cached;
    try {
      const answer = await api.get(id, cached?.server_version);
      if (!answer) {
        // Deleted in another browser: this copy is of nothing now.
        if (cached) await cache.remove(id);
        return null;
      }
      if ('notModified' in answer) return cached ?? null;
      return await adopt(answer, ownerId, cached);
    } catch (e) {
      if (isUnreachable(e) && cached) return cached;
      throw e;
    }
  }

  /**
   * The copy a WRITE is made from: the one here — the version the person was looking at — and
   * the server's only when there is none here. Deliberately not `fresh`: refreshing first would
   * lay this change over another browser's without either of them seeing it, which is the
   * overwrite the version exists to refuse.
   */
  async function base(id: string, ownerId: string): Promise<CachedRow | null> {
    return (await owned(id, ownerId)) ?? (await fresh(id, ownerId));
  }

  /** Send a whole project, made from `from`; the answer's version becomes the copy here. */
  async function push(row: CachedRow, from: number, messages: ChatMessage[]): Promise<CachedRow> {
    if (!online()) throw new ProjectNotSaved(NOT_SAVED_OFFLINE);
    let res: { version: number; updatedAt: string };
    try {
      res = await api.put(
        row.id,
        { name: row.name, description: row.description ?? '', definition: row.definition, messages },
        from,
      );
    } catch (e) {
      if (e instanceof ApiError && e.kind === 'offline') throw new ProjectNotSaved(NOT_SAVED_OFFLINE);
      if (e instanceof ApiError && e.kind === 'timeout') {
        unsure.add(row.id);
        throw new ProjectNotSaved(NOT_SAVED_TIMEOUT);
      }
      throw e;
    }
    unsure.delete(row.id);
    const next: CachedRow = {
      ...row,
      messages,
      server_version: res.version,
      version: res.version,
      updated_at: res.updatedAt,
      created_at: row.created_at || res.updatedAt,
    };
    await cache.put(next);
    if (row.server_version === undefined) await cache.dropLegacyMessages(row.id);
    return next;
  }

  /**
   * After a refusal: the server's version, here. `'gone'` when the server has no such project
   * any more — which the refusal must say, since "the latest version is loaded" would be false.
   * Null when it could not be asked; best effort, the refusal is said either way.
   */
  async function reload(id: string, ownerId: string): Promise<CachedRow | 'gone' | null> {
    try {
      const prev = await cache.get(id);
      const answer = await api.get(id);
      if (!answer) {
        await cache.remove(id);
        unsure.delete(id);
        return 'gone';
      }
      if ('notModified' in answer) return null;
      const row = await adopt(answer, ownerId, prev);
      // The copy here is the server's now, whatever the lost save did.
      unsure.delete(id);
      return row;
    } catch {
      return null;
    }
  }

  /** The refusal to throw after a 409 on `id`, true to what reloading found. */
  async function refusalAfterConflict(id: string, ownerId: string): Promise<Error> {
    const ownSaveUnsure = unsure.has(id);
    const reloaded = await reload(id, ownerId);
    if (reloaded === 'gone') return new ProjectDeletedElsewhere();
    return new ProjectChangedElsewhere(ownSaveUnsure ? LAST_SAVE_MAY_HAVE_LANDED : PROJECT_CHANGED_ELSEWHERE);
  }

  /** Add to (or clear) a project's chat, re-applied to the latest version if another write got there first. */
  async function changeChat(
    projectId: string,
    ownerId: string,
    change: (messages: ChatMessage[]) => ChatMessage[] | null,
  ): Promise<void> {
    for (let attempt = 0; attempt < 3; attempt++) {
      const row = await base(projectId, ownerId);
      if (!row) throw new Error(PROJECT_NOT_FOUND);
      const next = change(await messagesOf(row));
      if (!next) return;
      try {
        await push(row, row.server_version ?? 0, next);
        return;
      } catch (e) {
        if (!isConflict(e)) throw e;
        const reloaded = await reload(projectId, ownerId);
        if (reloaded === 'gone') throw new ProjectDeletedElsewhere();
        if (!reloaded) throw new ProjectChangedElsewhere();
      }
    }
    throw new ProjectChangedElsewhere();
  }

  /** Whose chat this is, for a caller that does not say: the account signed in, else the copy's. */
  async function chatOwner(projectId: string): Promise<string> {
    return deps.identity() ?? (await cache.get(projectId))?.owner_id ?? '';
  }

  const projects: ProjectStore = {
    async claimUnowned(ownerId: string): Promise<void> {
      if (!ownerId) return;
      const rows = await cache.all();
      const unowned = rows.filter((r) => r.owner_id === null || r.owner_id === undefined);
      if (unowned.length) {
        await write('projects', (s) => {
          for (const row of unowned) s.put({ ...row, owner_id: ownerId });
        });
      }
      if (!readable(ownerId)) return;
      await uploadLocalOnly(ownerId);
    },

    async getAll(ownerId: string): Promise<Project[]> {
      if (!readable(ownerId)) return [];
      const here = (await cache.all()).filter((r) => r.owner_id === ownerId);
      let rows: CachedRow[];
      if (!online()) {
        rows = here;
      } else {
        try {
          const listed = await api.list();
          const byId = new Map(here.map((r) => [r.id, r]));
          const onServer = await mapLimit(listed, 4, async (summary): Promise<CachedRow | null> => {
            const cached = byId.get(summary.id);
            if (cached && cached.server_version === summary.version) return cached;
            // The row's fields came with the list: no body for a row (see listRow).
            if (summary.listing) return listRow({ ...summary, listing: summary.listing }, ownerId);
            /*
             * A server that sends no list fields: the body it is. One that cannot be fetched is
             * that row's problem, not the list's — the copy here if there is one, else left out
             * this time. Failing the whole list hid every app over one.
             */
            try {
              const answer = await api.get(summary.id, cached?.server_version);
              if (!answer) return null;
              if ('notModified' in answer) return cached ?? null;
              return await adopt(answer, ownerId, cached);
            } catch (e) {
              console.warn('[projects] could not load one project for the list', {
                id: summary.id,
                error: e instanceof Error ? e.message : String(e),
              });
              return cached ?? null;
            }
          });
          const ids = new Set(listed.map((s) => s.id));
          // A copy of something the server no longer has was deleted in another browser.
          for (const r of here) if (r.server_version !== undefined && !ids.has(r.id)) await cache.remove(r.id);
          rows = [
            ...onServer.filter((r): r is CachedRow => r !== null),
            // Built here before projects moved to the server, and not uploaded yet: still listed.
            ...here.filter((r) => r.server_version === undefined && !ids.has(r.id)),
          ];
        } catch (e) {
          if (!isUnreachable(e)) throw e;
          rows = here;
        }
      }
      return rows.sort(byNewest).map(toProject);
    },

    async getById(id: string, ownerId: string): Promise<Project | null> {
      if (!readable(ownerId)) return null;
      const row = await fresh(id, ownerId);
      return row ? toProject(row) : null;
    },

    async create(project: NewProject, ownerId: string): Promise<Project> {
      writable(ownerId);
      const row: CachedRow = {
        id: newId(),
        name: project.name,
        description: project.description ?? '',
        definition: clone(project.definition),
        thumbnail: null,
        created_at: '',
        updated_at: '',
        is_published: false,
        version: 0,
        owner_id: ownerId,
        messages: [],
      };
      return toProject(await serially(row.id, () => push(row, 0, [])));
    },

    update(id: string, updates: ProjectUpdate, ownerId: string): Promise<void> {
      return serially(id, async () => {
        writable(ownerId);
        const row = await base(id, ownerId);
        if (!row) return;
        const next: CachedRow = { ...row };
        // Only the fields actually given — an undefined must not blank a stored value.
        if (updates.name !== undefined) next.name = updates.name;
        if (updates.description !== undefined) next.description = updates.description;
        if (updates.definition !== undefined) next.definition = clone(updates.definition);
        if (updates.thumbnail !== undefined) next.thumbnail = updates.thumbnail;
        if (updates.is_published !== undefined) next.is_published = updates.is_published;
        try {
          await push(next, row.server_version ?? 0, await messagesOf(row));
        } catch (e) {
          if (!isConflict(e)) throw e;
          throw await refusalAfterConflict(id, ownerId);
        }
      });
    },

    updateName(id: string, name: string, ownerId: string): Promise<void> {
      return projects.update(id, { name }, ownerId);
    },

    delete(id: string, ownerId: string): Promise<void> {
      return serially(id, async () => {
        writable(ownerId);
        if (!online()) throw new ProjectNotSaved(NOT_SAVED_OFFLINE);
        try {
          await api.remove(id);
        } catch (e) {
          if (isUnreachable(e)) throw new ProjectNotSaved(NOT_SAVED_OFFLINE);
          throw e;
        }
        await cache.remove(id);
        await cache.dropLegacyMessages(id);
      });
    },

    async duplicate(id: string, ownerId: string): Promise<Project> {
      const row = await projects.getById(id, ownerId);
      if (!row) throw new Error('That project does not exist.');
      const name = `${row.name} copy`;
      // A new, unpublished app with its own storage namespace — see `duplicateDefinition`.
      return projects.create(
        { name, description: row.description, definition: duplicateDefinition(row.definition, name) },
        ownerId,
      );
    },
  };

  /**
   * Upload every project that exists only in this browser, once each, by its own id. A 409 means
   * the server already has it — another tab's upload, or this one's whose answer was lost — and
   * its copy is taken. Anything else leaves the project here, listed, for next time.
   */
  async function uploadLocalOnly(ownerId: string): Promise<void> {
    const pending = (await cache.all()).filter((r) => r.owner_id === ownerId && r.server_version === undefined);
    for (const { id } of pending) {
      await serially(id, async () => {
        const row = await owned(id, ownerId);
        if (!row || row.server_version !== undefined) return;
        try {
          await push(row, 0, await messagesOf(row));
        } catch (e) {
          if (isConflict(e)) {
            await reload(id, ownerId);
            return;
          }
          console.warn('[projects] could not upload a project from this browser yet', {
            id,
            error: e instanceof Error ? e.message : String(e),
          });
        }
      }).catch(() => undefined);
    }
  }

  const chat: ChatStore = {
    async getByProjectId(projectId: string): Promise<ChatMessage[]> {
      const ownerId = await chatOwner(projectId);
      if (!readable(ownerId)) return [];
      const row = await fresh(projectId, ownerId);
      return row ? messagesOf(row) : [];
    },

    addMessage(projectId: string, message: Omit<ChatMessage, 'id'>): Promise<ChatMessage> {
      return serially(projectId, async () => {
        const ownerId = await chatOwner(projectId);
        writable(ownerId);
        const stored: ChatMessage = { ...message, id: newId() };
        await changeChat(projectId, ownerId, (messages) => [...messages, stored]);
        return stored;
      });
    },

    deleteByProjectId(projectId: string): Promise<void> {
      return serially(projectId, async () => {
        const ownerId = await chatOwner(projectId);
        if (!ownerId) return;
        const row = (await owned(projectId, ownerId)) ?? (online() ? await fresh(projectId, ownerId).catch(() => null) : null);
        if (!row) return;
        writable(ownerId);
        await changeChat(projectId, ownerId, (messages) => (messages.length ? [] : null));
      });
    },
  };

  return { projects, chat };
}
